import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import getGuestDisplayName from "utils/getGuestDisplayName";
import { AdminTable } from "components/shared/AdminTable";
import { Alert } from "components/ui/alert";

type GuestRecordResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  validFrom?: string;
  validTo?: string;
  deletedTimestamp?: string;
  guestTypes?: string[];
};

export default function GroupGuests({ groupId }: { groupId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["acg", "guests", groupId],
    queryFn: async () => {
      const response = await axios.get<GuestRecordResponse[]>(
        `${BASE_API_URL}/api/AccessControlGroups/${groupId}/guests`,
      );
      return response.data;
    },
  });

  if (isError) {
    return <Alert variant="destructive">Unable to load guests.</Alert>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
        Loading guests...
      </div>
    );
  }

  const guests = data ?? [];

  return (
    <AdminTable aria-label="Group guests">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Email</th>
          <th scope="col">Types</th>
          <th scope="col">Valid From</th>
          <th scope="col">Valid To</th>
          <th scope="col">Deleted</th>
        </tr>
      </thead>
      <tbody>
        {guests.map((guest) => (
          <tr key={guest.id}>
            <td>
              <Link to={`/admin/guests/${guest.id}`}>
                {getGuestDisplayName(guest.firstName, guest.lastName)}
              </Link>
              <div className="pt-1 text-xs text-slate-500">{guest.id}</div>
            </td>
            <td>{guest.email}</td>
            <td>{(guest.guestTypes || []).join(", ") || "—"}</td>
            <td>{guest.validFrom ? new Date(guest.validFrom).toLocaleString() : "—"}</td>
            <td>{guest.validTo ? new Date(guest.validTo).toLocaleString() : "—"}</td>
            <td>{guest.deletedTimestamp ? "Yes" : "No"}</td>
          </tr>
        ))}
        {guests.length === 0 && (
          <tr>
            <td colSpan={6} className="py-16 text-sm text-center text-slate-500">
              No guests found for this group.
            </td>
          </tr>
        )}
      </tbody>
    </AdminTable>
  );
}
